import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../api';
import type { AdminReportDto, ReportCountsDto } from '../../api';
import { ReportStatus } from '../../api/generated/boticaAPI.schemas';
import { Tabs, type TabItem } from '../../components/ui/Tab';
import { Table, type Column } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { REASON_LABEL, STATUS_BADGE, STATUS_LABEL, TARGET_LABEL, formatDate } from './reportLabels';

type TabValue = 'all' | 'pending' | 'actioned' | 'dismissed';

const TAB_STATUS: Record<TabValue, ReportStatus | undefined> = {
  all: undefined,
  pending: ReportStatus.Pending,
  actioned: ReportStatus.Actioned,
  dismissed: ReportStatus.Dismissed,
};

const EMPTY_MESSAGES: Record<TabValue, { title: string; desc: string }> = {
  all: {
    title: 'No reports yet',
    desc: 'Reports filed by members will appear here.',
  },
  pending: {
    title: 'Nothing to review',
    desc: 'All reports have been reviewed.',
  },
  actioned: {
    title: 'No actioned reports',
    desc: 'Reports you act on will appear here.',
  },
  dismissed: {
    title: 'No dismissed reports',
    desc: 'Reports you dismiss will appear here.',
  },
};

const PAGE_SIZE = 25;

export function ReportsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = (searchParams.get('tab') as TabValue | null) ?? 'pending';
  const [activeTab, setActiveTab] = useState<TabValue>(initialTab);
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState<AdminReportDto[]>([]);
  const [counts, setCounts] = useState<ReportCountsDto>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Every setState happens after an await, so none run synchronously during
  // the effect. The cancelled flag keeps a late response from writing to an
  // unmounted page.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const res = await adminApi.getReports();
        if (cancelled) return;
        setRows(res.reports ?? []);
        setCounts(res.counts ?? {});
      } catch {
        if (!cancelled) setError('Failed to load list. Try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as TabValue);
    setPage(1);
    setSearchParams({ tab }, { replace: true });
  };

  const filtered = useMemo(() => {
    const statusFilter = TAB_STATUS[activeTab];
    const base =
      statusFilter === undefined ? rows : rows.filter((r) => r.status === statusFilter);

    return [...base].sort((a, b) => {
      const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return sortDir === 'desc' ? bDate - aDate : aDate - bDate;
    });
  }, [rows, activeTab, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const tabs: TabItem[] = [
    { value: 'all', label: 'All', count: counts.total },
    { value: 'pending', label: 'Pending', count: counts.pending },
    { value: 'actioned', label: 'Actioned', count: counts.actioned },
    { value: 'dismissed', label: 'Dismissed', count: counts.dismissed },
  ];

  const columns: Column<AdminReportDto>[] = [
    {
      key: 'target',
      header: 'Target',
      render: (r) => (
        <span style={{ fontWeight: 500 }}>{TARGET_LABEL[r.targetType ?? 0] ?? '—'}</span>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (r) => (
        <span style={{ color: 'var(--text-secondary)' }}>
          {REASON_LABEL[r.reason ?? 0] ?? '—'}
        </span>
      ),
    },
    {
      key: 'reporter',
      header: 'Reported by',
      render: (r) => (
        <span style={{ color: 'var(--text-secondary)' }}>{r.reporterFullName ?? '—'}</span>
      ),
    },
    {
      key: 'count',
      header: 'Reports',
      // A single complaint and a pile-on need different responses, so the
      // count against the same target is worth seeing before opening.
      render: (r) => {
        const total = r.totalReportsForTarget ?? 1;
        return total > 1 ? (
          <Badge variant="pending">{total}</Badge>
        ) : (
          <span style={{ color: 'var(--text-placeholder)' }}>1</span>
        );
      },
    },
    {
      key: 'filed',
      header: 'Filed',
      render: (r) => (
        <span style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
          {formatDate(r.createdAt)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => {
        const v = r.status ?? 0;
        return <Badge variant={STATUS_BADGE[v]}>{STATUS_LABEL[v]}</Badge>;
      },
    },
  ];

  const empty = EMPTY_MESSAGES[activeTab];

  return (
    <div style={{ maxWidth: 'var(--content-max-width)' }}>
      <Tabs value={activeTab} onChange={handleTabChange} items={tabs} />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          margin: '16px 0 12px',
          minHeight: 32,
        }}
      >
        {error ? (
          <span style={{ color: 'var(--status-unavailable)', fontSize: 'var(--text-sm)' }}>
            {error}
          </span>
        ) : (
          <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
            {loading ? 'Loading…' : `${filtered.length} report${filtered.length === 1 ? '' : 's'}`}
          </span>
        )}

        <Button
          variant="secondary"
          onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
        >
          {sortDir === 'desc' ? 'Newest first' : 'Oldest first'}
        </Button>
      </div>

      <Table
        columns={columns}
        rows={pageRows}
        getKey={(r) => r.id ?? ''}
        loading={loading}
        emptyMessage={empty.title}
        emptyDescription={empty.desc}
        onRowClick={(r) => navigate(`/reports/${r.id}`)}
      />

      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 12,
          }}
        >
          <Button variant="secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
            Page {page} of {totalPages}
          </span>
          <Button
            variant="secondary"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
